/**
 * Genie Canvas Node Factory
 *
 * Helper functions to create Genie canvas nodes
 */

import type { GenieCanvasNode, CreateGenieNodeOptions } from '../types';
import type { NodeType, GenieNodeState } from '@genieartinc/schemas';
import { NODE_DIMENSIONS, NODE_VISUAL_CONSTANTS, NODE_TITLE_TEMPLATES } from '@genieartinc/schemas';
import { getHandles } from '../configs';

/**
 * In-memory counter for node titles
 * Tracks how many nodes of each type have been created in the current session
 */
const nodeTypeCounters: Record<string, number> = {};

/**
 * Generate automatic title for a node
 * @param nodeType - Node type (e.g., 'text-file', 'image', 'text')
 * @param purpose - Node purpose (for image nodes: 'create', 'edit', 'enhance')
 * @returns Generated title (e.g., "Text File 1", "Image Create 2", "Image Enhance 1")
 */
function generateNodeTitle(nodeType: string, purpose?: 'create' | 'edit' | 'enhance' | 'tools'): string {
  // Special handling for nodes with purpose (image, video, 3d)
  if ((nodeType === 'image' || nodeType === 'video' || nodeType === '3d') && purpose) {
    const typeLabel = nodeType === '3d' ? '3D' : nodeType.charAt(0).toUpperCase() + nodeType.slice(1);
    const purposeLabel = purpose.charAt(0).toUpperCase() + purpose.slice(1);
    const template = `${typeLabel} ${purposeLabel}`;

    // Use composite key for counter (image-create, video-edit, 3d-enhance)
    const counterKey = `${nodeType}-${purpose}`;
    if (!nodeTypeCounters[counterKey]) {
      nodeTypeCounters[counterKey] = 0;
    }
    nodeTypeCounters[counterKey]++;

    return `${template} ${nodeTypeCounters[counterKey]}`;
  }

  // Get base template from NODE_TITLE_TEMPLATES
  const template = NODE_TITLE_TEMPLATES[nodeType] || 'Node';

  // Increment counter for this node type
  if (!nodeTypeCounters[nodeType]) {
    nodeTypeCounters[nodeType] = 0;
  }
  nodeTypeCounters[nodeType]++;

  // Return formatted title
  return `${template} ${nodeTypeCounters[nodeType]}`;
}

/**
 * Reset node type counters (useful for testing or clearing canvas)
 */
export function resetNodeCounters() {
  Object.keys(nodeTypeCounters).forEach(key => {
    delete nodeTypeCounters[key];
  });
}

/**
 * Initialize counters from existing nodes (call when loading a canvas).
 * Parses node titles to find the highest counter value per type,
 * so new nodes continue numbering from where existing titles left off.
 */
export function initializeCountersFromNodes(nodes: Array<{ title?: string; type?: string; purpose?: string }>) {
  // Reset first
  resetNodeCounters();

  // Build a reverse lookup: title template value -> counter key
  // e.g. "Text File" -> "text-file", "Video node" -> "video"
  const templateToKey: Record<string, string> = {};
  for (const [key, template] of Object.entries(NODE_TITLE_TEMPLATES)) {
    templateToKey[template] = key;
  }

  // Purpose-based patterns for image/video/3d nodes with purpose sub-types
  const purposePatterns: Record<string, string> = {
    'Image Create': 'image-create',
    'Image Edit': 'image-edit',
    'Image Enhance': 'image-enhance',
    'Image Tools': 'image-tools',
    'Video Create': 'video-create',
    'Video Edit': 'video-edit',
    'Video Enhance': 'video-enhance',
    '3D Create': '3d-create',
    '3D Edit': '3d-edit',
    '3D Enhance': '3d-enhance',
  };

  for (const node of nodes) {
    const title = node.title;
    if (!title) continue;

    // Match pattern: "Some Name 123" — extract the trailing number
    const match = title.match(/^(.+?)\s+(\d+)$/);
    if (!match) continue;

    const [, prefix, numStr] = match;
    const num = parseInt(numStr, 10);
    if (isNaN(num)) continue;

    // Find the counter key that would produce this prefix
    let counterKey: string | null = null;

    // Check purpose-based keys first (e.g., "Image Create" -> "image-create")
    if (purposePatterns[prefix]) {
      counterKey = purposePatterns[prefix];
    } else if (templateToKey[prefix]) {
      // Check NODE_TITLE_TEMPLATES reverse lookup (e.g., "Text File" -> "text-file")
      counterKey = templateToKey[prefix];
    }

    if (counterKey) {
      nodeTypeCounters[counterKey] = Math.max(nodeTypeCounters[counterKey] || 0, num);
    }
  }
}

/**
 * Decrement node counter when a node is deleted
 * @param nodeType - Node type to decrement
 */
export function decrementNodeCounter(nodeType: string) {
  if (nodeTypeCounters[nodeType] && nodeTypeCounters[nodeType] > 0) {
    nodeTypeCounters[nodeType]--;
  }
}

/**
 * Create a Genie canvas node
 *
 * @example
 * const textNode = createGenieNode({
 *   type: 'text',
 *   position: { x: 100, y: 100 },
 *   componentType: 'TextFile',
 *   initialState: { prompt: 'Hello' }
 * })
 */
export function createGenieNode(options: CreateGenieNodeOptions): GenieCanvasNode {
  const {
    type,
    position,
    componentType,
    initialState = {},
    handleOverrides,
    dimensions,
    inverseScale,
    zIndex,
  } = options;

  const nodeId = `${componentType.toLowerCase()}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Use provided dimensions or default for component type
  const nodeDimensions = dimensions || NODE_DIMENSIONS[componentType] || { width: 500, height: 500 };

  // Generate automatic title if not provided in initialState
  // For media nodes with purpose (image, video, 3d), pass purpose from initialState
  const purpose = (type === 'image' || type === 'video' || type === '3d') ? (initialState.purpose as 'create' | 'edit' | 'enhance' | 'tools' | undefined) : undefined;
  const nodeTitle = initialState.title || generateNodeTitle(type, purpose);

  return {
    id: nodeId,
    type,
    position,
    dimensions: nodeDimensions,
    titleHeight: NODE_VISUAL_CONSTANTS.title.height, // Add title height for handle positioning
    handles: getHandles(componentType, handleOverrides),
    inverseScale, // Optional: if true, content maintains size at all zoom levels
    zIndex, // Initial z-index (if provided)
    component: {
      type: componentType,
      state: {
        collapsed: false, // Default to expanded
        purpose: 'create' as const, // Default purpose
        previewFps: 60, // Default FPS for shader nodes (matches schema default)
        ...initialState,
        title: nodeTitle, // Add generated title to state
        titleSource: initialState.titleSource ?? (initialState.title ? 'custom' : 'automatic'),
        // Settings persistence (for nodes with settings panels)
        modelId: initialState.modelId || undefined,
        settings: initialState.settings || {},
      },
      config: {
        autoResize: true,
        draggableArea: 'auto',
      },
    },
    data: {
      nodeType: type as NodeType,
      config: {
        type: type as NodeType,
        title: componentType,
        icon: getNodeIcon(componentType),
        fields: [],
      },
    },
  };
}

/**
 * Create a TextFile node
 */
export function createTextFileNode(
  position: { x: number; y: number },
  initialText?: string,
  parentNodeId?: string  // Parent node ID (for generated items)
): GenieCanvasNode {
  const node = createGenieNode({
    type: 'text-file',
    position,
    componentType: 'TextFile',
    initialState: {
      purpose: 'create' as const,
      prompt: initialText || '', // TextFile adapter uses 'prompt' field
      parentNodeId,              // Store parent ID in state
    },
    inverseScale: false, // Node dimensions stay fixed, content scales normally
  });

  return node;
}

/**
 * Create an ImageFile node
 */
export function createImageFileNode(
  position: { x: number; y: number },
  initialImage?: string | File,
  parentNodeId?: string,  // Parent node ID (for generated items)
  imageDimensions?: { width: number; height: number }  // Optional: image dimensions from metadata
): GenieCanvasNode {
  // If initialImage is a URL string, set it as fileMetadata.publicUrl
  const initialState: Partial<GenieNodeState> & Record<string, unknown> = {
    purpose: 'create' as const,
  };
  let nodeDimensions: { width: number; height: number } | undefined;

  if (typeof initialImage === 'string') {
    initialState.fileMetadata = {
      publicUrl: initialImage,
      filename: 'image.jpg',
      size: 0,
      contentType: 'image/jpeg',
      // Include dimensions in metadata if provided
      ...(imageDimensions ? { width: imageDimensions.width, height: imageDimensions.height } : {})
    };

    // Use standard square dimensions for ImageFile (500x500)
    // Image aspect ratio is handled inside the component with object-fit
  } else if (initialImage instanceof File) {
    // For File objects, we'll need to upload them - store temporarily
    initialState.pendingFile = initialImage;
  }

  // Add parent ID
  if (parentNodeId) {
    initialState.parentNodeId = parentNodeId;
  }

  const node = createGenieNode({
    type: 'image-file',
    position,
    componentType: 'ImageFile',
    initialState,
    dimensions: nodeDimensions,  // Use calculated dimensions if available
    inverseScale: false, // Node dimensions stay fixed, content scales normally
  });

  return node;
}

/**
 * Create a VideoFile node
 */
export function createVideoFileNode(
  position: { x: number; y: number },
  initialVideo?: string,
  parentNodeId?: string,
): GenieCanvasNode {
  const initialState: Partial<GenieNodeState> & Record<string, unknown> = {};

  if (typeof initialVideo === 'string') {
    initialState.fileMetadata = {
      publicUrl: initialVideo,
      filename: 'video.mp4',
      size: 0,
      contentType: 'video/mp4',
    };
  }

  if (parentNodeId) {
    initialState.parentNodeId = parentNodeId;
  }

  return createGenieNode({
    type: 'video-file',
    position,
    componentType: 'VideoFile',
    initialState,
    inverseScale: false,
  });
}

/**
 * Create an AudioFile node — holds one audio file and offers it on an
 * `audio-output` handle, e.g. to drive an audio-reactive shader.
 */
export function createAudioFileNode(
  position: { x: number; y: number },
  initialAudio?: string,
  parentNodeId?: string,
): GenieCanvasNode {
  const initialState: Partial<GenieNodeState> & Record<string, unknown> = {};

  if (typeof initialAudio === 'string') {
    initialState.fileMetadata = {
      publicUrl: initialAudio,
      filename: 'audio',
      size: 0,
      contentType: 'audio/mpeg',
    };
  }

  if (parentNodeId) {
    initialState.parentNodeId = parentNodeId;
  }

  return createGenieNode({
    type: 'audio-file',
    position,
    componentType: 'AudioFile',
    initialState,
    inverseScale: false,
  });
}

/**
 * Create a LoraModel node — represents a user's trained LoRA as a connected node.
 * Drop from the Models tab in the sidebar spawns one of these at the cursor.
 */
export interface LoraModelNodeMeta {
  id: string;           // user_lora row id
  name: string;
  triggerWord?: string | null;
  baseModel?: string | null;
  thumbnailUrl?: string | null;
}

export function createLoraModelNode(
  position: { x: number; y: number },
  meta: LoraModelNodeMeta,
): GenieCanvasNode {
  return createGenieNode({
    type: 'lora-model',
    position,
    componentType: 'LoraModel',
    initialState: {
      loraId: meta.id,
      title: meta.name,
      triggerWord: meta.triggerWord ?? null,
      baseModel: meta.baseModel ?? null,
      thumbnailUrl: meta.thumbnailUrl ?? null,
      scale: 1,
    },
  });
}

/**
 * Create a TextNode with text prompt input only
 */
export function createTextNode(
  position: { x: number; y: number }
): GenieCanvasNode {
  return createGenieNode({
    type: 'text',
    position,
    componentType: 'TextNode',
    initialState: {
      purpose: 'create' as const,
      createdItems: [],
    },
    handleOverrides: [
      {
        id: 'prompt-input',
        type: 'input',
        position: { side: 'left', offsetY: 0.11 },
        dataType: 'text',
        maxConnections: 1,
      },
      {
        id: 'generated-output',
        type: 'output',
        position: { side: 'right', offsetY: 0.11 },
        dataType: 'text',
        locked: true,
      },
    ],
  });
}

/**
 * Create an ImageNode with prompt inputs
 */
export function createImageNode(
  position: { x: number; y: number },
  purpose: 'create' | 'edit' | 'enhance' | 'tools' = 'create'
): GenieCanvasNode {
  return createGenieNode({
    type: 'image',
    position,
    componentType: 'ImageNode',
    initialState: {
      purpose,
      createdItems: [],
    },
    handleOverrides: [
      {
        id: 'prompt-input',
        type: 'input',
        position: { side: 'left', offsetY: 0.11 },
        dataType: 'text',
        maxConnections: 1,
      },
      {
        id: 'image-input',
        type: 'input',
        position: { side: 'left', ordered: 1 },
        dataType: 'image',
        maxConnections: 1,
      },
      {
        id: 'generated-output',
        type: 'output',
        position: { side: 'right', offsetY: 0.11 },
        dataType: 'image',
        locked: true,
      },
    ],
  });
}

/**
 * Create a VideoNode with prompt inputs
 */
export function createVideoNode(
  position: { x: number; y: number },
  purpose: 'create' | 'edit' | 'enhance' | 'tools' = 'create'
): GenieCanvasNode {
  return createGenieNode({
    type: 'video',
    position,
    componentType: 'VideoNode',
    initialState: {
      purpose,
      createdItems: [],
    },
    handleOverrides: purpose === 'create'
      ? [
          {
            id: 'prompt-input',
            type: 'input',
            position: { side: 'left', ordered: 0 },
            dataType: 'text',
            maxConnections: 1,
          },
          {
            id: 'image-input',
            type: 'input',
            position: { side: 'left', ordered: 1 },
            dataType: 'image',
            maxConnections: 1,
          },
          {
            id: 'generated-output',
            type: 'output',
            position: { side: 'right', offsetY: 0.11 },
            dataType: 'video',
            locked: true,
          },
        ]
      : [
          {
            id: 'video-input',
            type: 'input',
            position: { side: 'left', ordered: 0 },
            dataType: 'video',
            maxConnections: 1,
          },
          {
            id: 'prompt-input',
            type: 'input',
            position: { side: 'left', ordered: 1 },
            dataType: 'text',
            maxConnections: 1,
          },
          {
            id: 'generated-output',
            type: 'output',
            position: { side: 'right', offsetY: 0.11 },
            dataType: 'video',
            locked: true,
          },
        ],
  });
}

/**
 * Create a 3D Node with prompt inputs
 */
export function createNode3D(
  position: { x: number; y: number },
  purpose: 'create' | 'edit' | 'enhance' | 'tools' = 'create'
): GenieCanvasNode {
  return createGenieNode({
    type: '3d',
    position,
    componentType: 'ThreeDNode',
    initialState: {
      purpose,
      createdItems: [],
    },
    handleOverrides: [
      {
        id: 'prompt-input',
        type: 'input',
        position: { side: 'left', offsetY: 0.11 },
        dataType: 'text',
        maxConnections: 1,
      },
    ],
  });
}

/**
 * Create a ShaderNode with multi-modal inputs and outputs
 */
export function createShaderNode(
  position: { x: number; y: number }
): GenieCanvasNode {
  return createGenieNode({
    type: 'shader',
    position,
    componentType: 'ShaderNode',
    initialState: {
      runtimeMode: 'glsl',
      collapsed: false,
      createdItems: [],
    },
    handleOverrides: [
      {
        id: 'image-input',
        type: 'input',
        position: { side: 'left', offsetY: 0.5 },
        dataType: 'image',
        maxConnections: 1,
        label: 'Image',
      },
      {
        id: 'image-output',
        type: 'output',
        position: { side: 'right', offsetY: 0.5 },
        dataType: 'image',
        label: 'Out',
      },
    ],
  });
}


/**
 * Create a CommentNode (annotation pin)
 */
export function createCommentNode(
  position: { x: number; y: number }
): GenieCanvasNode {
  return createGenieNode({
    type: 'comment',
    position,
    componentType: 'CommentNode',
    initialState: {
      comment: '',
      resolved: false,
    },
    dimensions: { width: 44, height: 44 },
  });
}




/**
 * Create a DataTable node (series pipeline source)
 */
export function createDataTableNode(
  position: { x: number; y: number },
): GenieCanvasNode {
  return createGenieNode({
    type: 'data-table',
    position,
    componentType: 'DataTable',
    initialState: {
      purpose: 'create' as const,
      categories: [],
    },
    inverseScale: false,
  });
}

/**
 * Create a VariationGenerator node (series pipeline variation engine)
 */
export function createVariationGeneratorNode(
  position: { x: number; y: number },
): GenieCanvasNode {
  return createGenieNode({
    type: 'variation-generator',
    position,
    componentType: 'VariationGenerator',
    initialState: {
      purpose: 'create' as const,
      count: 1000,
      variations: [],
      generationStatus: 'idle',
      generationStats: null,
      conflictRules: [],
    },
    inverseScale: false,
  });
}

/**
 * Create a PromptComposer node (series pipeline prompt writer)
 */
export function createPromptComposerNode(
  position: { x: number; y: number },
): GenieCanvasNode {
  return createGenieNode({
    type: 'prompt-composer',
    position,
    componentType: 'PromptComposer',
    initialState: {
      purpose: 'create' as const,
      template: '',
      mode: 'freeform',
      temperature: 0.7,
      maxTokensPerPrompt: 200,
      negativePromptTemplate: '',
      composedPrompts: [],
      compositionStatus: 'idle',
      compositionStats: null,
    },
    inverseScale: false,
  });
}

/**
 * Create a BatchGenerator node (series pipeline batch orchestrator)
 */
export function createBatchGeneratorNode(
  position: { x: number; y: number },
): GenieCanvasNode {
  return createGenieNode({
    type: 'batch-generator',
    position,
    componentType: 'BatchGenerator',
    initialState: {
      purpose: 'create' as const,
      batchStatus: 'idle',
      mediaType: null,
      progress: { total: 0, succeeded: 0, failed: 0, queued: 0, currentIndex: 0 },
      cost: { totalCost: 0, projectedTotal: 0 },
      config: { concurrency: 1, maxRetries: 3, pauseOnError: false, skipCompleted: true },
    },
    inverseScale: false,
  });
}

/**
 * Create a GalleryGroup node (container for batch-generated outputs)
 */
export function createGalleryGroupNode(
  position: { x: number; y: number },
  seriesName?: string,
): GenieCanvasNode {
  return createGenieNode({
    type: 'gallery-group',
    position,
    componentType: 'GalleryGroup',
    initialState: {
      purpose: 'create' as const,
      seriesName: seriesName || 'Gallery',
      columns: 10,
      cellSize: 200,
      totalImages: 0,
      approvedCount: 0,
      rejectedCount: 0,
      collapsed: false,
    },
    dimensions: { width: 2200, height: 2200 },
    inverseScale: false,
  });
}

/**
 * Create a GalleryContainer node (enter-inside container for batch outputs)
 */
export function createGalleryContainerNode(
  position: { x: number; y: number },
  seriesName?: string,
): GenieCanvasNode {
  return createGenieNode({
    type: 'gallery-container',
    position,
    componentType: 'GalleryContainer',
    initialState: {
      purpose: 'create' as const,
      seriesName: seriesName || 'Gallery',
      totalItems: 0,
      mediaType: null,
    },
    inverseScale: false,
  });
}

/**
 * Create a MaskNode with brush painting overlay
 */
export function createMaskNode(
  position: { x: number; y: number }
): GenieCanvasNode {
  return createGenieNode({
    type: 'mask',
    position,
    componentType: 'MaskNode',
    initialState: {
      purpose: 'tools' as const,
      images: [],
    },
  });
}

/**
 * Get icon for node type
 */
function getNodeIcon(componentType: string): string {
  const icons: Record<string, string> = {
    TextFile: 'type',
    ImageFile: 'image',
    VideoFile: 'video',
    AudioFile: 'music',
    VideoNode: 'video',
    ImageNode: 'image',
    TextNode: 'type',
    ShaderNode: 'sparkles',
    ThreeDNode: 'box',
    DataTable: 'table',
    VariationGenerator: 'shuffle',
    PromptComposer: 'pen-line',
    BatchGenerator: 'play',
    GalleryGroup: 'layout-grid',
    GalleryContainer: 'layers',
    CommentNode: 'message-circle',
    MaskNode: 'paintbrush',
    LoraModel: 'sparkles',
  };

  return icons[componentType] || 'circle';
}
